import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  XCircle,
  ExternalLink,
  Leaf,
  Scale,
  ShieldCheck,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ErrorState, PageLoader } from '../components/states';
import { Brand } from '../components/layouts';
import { Button, ButtonLink, Card, FormField, InlineNotice, PageHeading } from '../components/ui';
import { useAuth } from '../features/auth/AuthContext';
import { api, getErrorMessage } from '../lib/api';
import { env } from '../lib/env';
import { formatDate, safeInternalPath } from '../lib/format';
import type { LegalDocument } from '../lib/schemas';

export function AuthExchangePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signInWithPortalToken, user } = useAuth();
  const code = searchParams.get('code');
  const returnTo = safeInternalPath(searchParams.get('returnTo'));
  const consumedCode = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code || consumedCode.current === code) return;
    consumedCode.current = code;
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('code');
    window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}`);
    void api
      .exchangeCode(code)
      .then(async (result) => {
        await signInWithPortalToken(result.customToken);
        void navigate(safeInternalPath(result.redirectPath, returnTo), { replace: true });
      })
      .catch((exchangeError: unknown) => {
        setError(getErrorMessage(exchangeError));
      });
  }, [code, navigate, returnTo, signInWithPortalToken]);

  if (code && !error) {
    return (
      <section className="auth-exchange">
        <PageLoader label="Hesabınız güvenle bağlanıyor…" />
        <p>Bu işlem genellikle birkaç saniye sürer. Sayfayı kapatmayın.</p>
      </section>
    );
  }

  const loginUrl = new URL('/account/login', env.shopifyStorefrontUrl);
  loginUrl.searchParams.set('return_url', '/apps/abonelik/portal');

  return (
    <section className="auth-exchange">
      <Card className="auth-card">
        <div className="auth-card__brand">
          <Brand />
        </div>
        <div aria-hidden="true" className="auth-card__illustration">
          <Leaf size={42} />
        </div>
        <p className="eyebrow">Güvenli müşteri alanı</p>
        <h1>
          {error ? 'Bağlantı yenilenmeli' : user ? 'Hesabınız hazır' : 'Aboneliklerinize ulaşın'}
        </h1>
        <p>
          {error
            ? 'Tek kullanımlık giriş bağlantınızın süresi dolmuş veya bağlantı daha önce kullanılmış olabilir.'
            : 'Tropikal Akdeniz hesabınızla devam ederek yalnızca size ait kutuları görüntüleyebilirsiniz.'}
        </p>
        {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}
        {user ? (
          <ButtonLink to={returnTo}>Aboneliklerime geç</ButtonLink>
        ) : (
          <a className="button button--primary button--regular" href={loginUrl.toString()}>
            Tropikal Akdeniz hesabımla devam et <ExternalLink aria-hidden="true" size={17} />
          </a>
        )}
        <span className="auth-card__assurance">
          <ShieldCheck aria-hidden="true" size={16} /> Kart bilgileriniz portalımıza ulaşmaz.
        </span>
      </Card>
    </section>
  );
}

export function AdminLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signInAdminWithEmail, signInAdminWithGoogle, user } = useAuth();
  const returnTo = safeInternalPath(searchParams.get('returnTo'), '/admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<'email' | 'google' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.isAdmin) void navigate(returnTo, { replace: true });
  }, [navigate, returnTo, user]);

  async function completeLogin(action: () => Promise<unknown>, mode: 'email' | 'google') {
    setBusy(mode);
    setError(null);
    try {
      await action();
      await navigate(returnTo, { replace: true });
    } catch {
      setError(
        "Giriş doğrulanamadı. Hesabın yönetici claim'ini ve izin verilen e-posta listesini kontrol edin.",
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="auth-exchange">
      <Card className="auth-card">
        <div className="auth-card__brand">
          <Brand />
        </div>
        <p className="eyebrow">Operasyon paneli</p>
        <h1>Yönetici girişi</h1>
        <p>
          Yalnız Firebase Authentication üzerinde <code>admin=true</code> custom claim'i bulunan
          hesaplar kabul edilir.
        </p>
        {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}
        <form
          className="form-stack"
          onSubmit={(event) => {
            event.preventDefault();
            void completeLogin(() => signInAdminWithEmail(email, password), 'email');
          }}
        >
          <FormField htmlFor="admin-email" label="E-posta" required>
            <input
              autoComplete="username"
              id="admin-email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </FormField>
          <FormField htmlFor="admin-password" label="Parola" required>
            <input
              autoComplete="current-password"
              id="admin-password"
              minLength={8}
              onChange={(event) => setPassword(event.target.value)}
              required
              type="password"
              value={password}
            />
          </FormField>
          <Button loading={busy === 'email'} type="submit">
            E-posta ile giriş yap
          </Button>
        </form>
        <Button
          loading={busy === 'google'}
          onClick={() => void completeLogin(signInAdminWithGoogle, 'google')}
          type="button"
          variant="secondary"
        >
          Google ile giriş yap
        </Button>
        <span className="auth-card__assurance">
          <ShieldCheck aria-hidden="true" size={16} /> Yetki her istekte backend tarafından yeniden
          doğrulanır.
        </span>
      </Card>
    </section>
  );
}

export function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const subscriptionId = searchParams.get('subscriptionId');
  const query = useQuery({
    queryKey: ['payment-result', subscriptionId],
    queryFn: ({ signal }) => {
      if (!subscriptionId) throw new Error('İşlem bilgisi bulunamadı.');
      return api.getPaymentResult(subscriptionId, signal);
    },
    enabled: Boolean(subscriptionId && user),
    retry: 2,
    refetchInterval: (result) => (result.state.data?.status === 'PROCESSING' ? 3_000 : false),
  });

  // Somebody who subscribed without an account has no session to read the result with,
  // so the outcome the callback put in the URL is all there is to go on. Showing them
  // "still checking" for a declined card — or for a successful one — is the one thing this
  // page must not do: they have just been charged, or just failed to be.
  if (!user || !subscriptionId) {
    const outcome = searchParams.get('status');
    if (outcome === 'failure') {
      return (
        <section className="result-page">
          <Card className="result-card" role="status">
            <XCircle aria-hidden="true" className="result-card__icon result-card__icon--failure" />
            <p className="eyebrow">İşlem sonucu</p>
            <h1>Ödeme tamamlanamadı</h1>
            <p>
              Kartınızdan tahsilat yapılmadı ve aboneliğiniz başlatılmadı. Dilerseniz başka bir
              kartla yeniden deneyebilirsiniz.
            </p>
            <a
              className="button"
              href={`${env.shopifyStorefrontUrl}${env.subscriptionProductPath}`}
            >
              Yeniden dene
            </a>
          </Card>
        </section>
      );
    }
    if (outcome === 'success') {
      return (
        <section className="result-page">
          <Card className="result-card" role="status">
            <CheckCircle2
              aria-hidden="true"
              className="result-card__icon result-card__icon--success"
            />
            <p className="eyebrow">İşlem sonucu</p>
            <h1>Aboneliğiniz başladı</h1>
            <p>
              Ödemeniz alındı. Siparişinizin onayını e-posta ile gönderiyoruz ve aboneliğiniz için
              hesabınızı oluşturduk: Aboneliklerim sayfasına ödeme sırasında girdiğiniz e-posta
              adresiyle giriş yaparak teslimatlarınızı görebilir, adresinizi değiştirebilir veya
              iptal edebilirsiniz.
            </p>
            <a className="button" href={`${env.shopifyStorefrontUrl}/account`}>
              Aboneliklerim'e git
            </a>
          </Card>
        </section>
      );
    }
    return (
      <section className="result-page">
        <Card className="result-card">
          <Clock3 aria-hidden="true" className="result-card__icon result-card__icon--processing" />
          <p className="eyebrow">İşlem sonucu</p>
          <h1>Sonuç güvenle kontrol ediliyor</h1>
          <p>
            Bankanızdan yanıt bekleniyor. Sonucu e-posta ile bildireceğiz; aboneliğiniz başladıysa
            Aboneliklerim sayfasında görünecek.
          </p>
          <a className="button" href={`${env.shopifyStorefrontUrl}/account`}>
            Aboneliklerim'e git
          </a>
        </Card>
      </section>
    );
  }

  if (query.isPending) return <PageLoader label="Ödeme sonucu sunucudan doğrulanıyor…" />;
  if (query.isError) {
    return (
      <section className="result-page">
        <ErrorState
          message={getErrorMessage(query.error)}
          onRetry={() => void query.refetch()}
          title="Sonuç henüz alınamadı"
        />
      </section>
    );
  }

  const isSuccess = query.data.status === 'SUCCEEDED';
  const isFailure = query.data.status === 'FAILED';
  const accountUrl = query.data.accountUrl ?? `${env.shopifyStorefrontUrl}/account`;
  const storefrontUrl = query.data.storefrontUrl ?? env.shopifyStorefrontUrl;
  return (
    <section className="result-page">
      {isSuccess ? <AccountForward url={accountUrl} /> : null}
      <Card className="result-card" role="status">
        {isSuccess ? (
          <CheckCircle2
            aria-hidden="true"
            className="result-card__icon result-card__icon--success"
          />
        ) : isFailure ? (
          <AlertCircle
            aria-hidden="true"
            className="result-card__icon result-card__icon--failure"
          />
        ) : (
          <Clock3 aria-hidden="true" className="result-card__icon result-card__icon--processing" />
        )}
        <p className="eyebrow">Sunucudan doğrulandı</p>
        <h1>
          {isSuccess
            ? 'Aboneliğiniz başladı'
            : isFailure
              ? 'Ödeme tamamlanamadı'
              : 'Ödemeniz kontrol ediliyor'}
        </h1>
        <p>{query.data.message}</p>
        {isSuccess ? (
          <a className="button" href={accountUrl}>
            Aboneliklerim'e git
          </a>
        ) : isFailure ? (
          <a className="button" href={storefrontUrl}>
            Ürün sayfasına dön ve tekrar dene
          </a>
        ) : (
          <a className="button" href={accountUrl}>
            Aboneliklerim'e git
          </a>
        )}
        <small>
          Siparişiniz yalnızca doğrulanmış başarılı tahsilattan sonra hesabınızda oluşturulur.
        </small>
      </Card>
    </section>
  );
}

/** A verified payment walks straight over to Aboneliklerim after a short beat. */
function AccountForward({ url }: { url: string }) {
  useEffect(() => {
    if (env.useMockApi) return;
    const timer = window.setTimeout(() => window.location.replace(url), 1_800);
    return () => window.clearTimeout(timer);
  }, [url]);
  return null;
}

const legalPageCopy: Record<
  LegalDocument['type'],
  { eyebrow: string; title: string; lead: string; points: string[] }
> = {
  subscription_terms: {
    eyebrow: 'Abonelik sözleşmesi',
    title: 'Düzenli teslimat ve ödeme koşulları',
    lead: 'Dönemsel tahsilat, teslimat, fiyat değişikliği, iptal ve taze ürün hükümlerinin geçerli sürümü.',
    points: [
      'Tahsilat tutarı ve sıklığı onay ekranında açıkça gösterilir.',
      'Gelecek tahsilatlar portal üzerinden abonelik iptal edilerek durdurulabilir.',
      'Önceden tahsil edilmiş siparişler otomatik olarak iade edilmez; iade ayrıca değerlendirilir.',
    ],
  },
  privacy_policy: {
    eyebrow: 'KVKK aydınlatma metni',
    title: 'Abonelik portalında kişisel verileriniz',
    lead: 'Veri sorumlusu, Shopify, iyzico ve Firebase veri akışları, saklama süreleri ve başvuru hakları.',
    points: [
      'Kart numarası ve CVV Tropikal Akdeniz sistemlerine ulaşmaz.',
      'Adres bilgisi yalnızca abonelik teslimatı ve ilgili operasyonlar için kullanılır.',
      'Yetkili olmayan hesaplar başka müşterilerin abonelik kayıtlarına erişemez.',
    ],
  },
  distance_sales_placeholder: {
    eyebrow: 'Mesafeli satış',
    title: 'Siparişe özgü satış, teslimat ve iade koşulları',
    lead: "İşlem özetiyle birlikte uygulanan Mesafeli Satış Sözleşmesi'nin geçerli sürümü.",
    points: [
      'Ürün içeriği, toplam bedel ve kargonun dahil olup olmadığı sipariş öncesinde gösterilir.',
      'Teslimat bölgesi ve tahmini hazırlık süresi sipariş şartlarında açıklanır.',
      'Cayma ve iade koşulları ürün niteliğine uygun biçimde hukuk incelemesinden sonra yayınlanır.',
    ],
  },
};

export function LegalPage({ type }: { type: LegalDocument['type'] }) {
  const copy = legalPageCopy[type];
  const query = useQuery({
    queryKey: ['legal', type],
    queryFn: ({ signal }) => api.getLegalDocument(type, signal),
    retry: 1,
  });

  return (
    <article className="legal-page">
      <a className="back-link" href={env.shopifyStorefrontUrl}>
        <ArrowLeft aria-hidden="true" size={17} /> Mağazaya dön
      </a>
      <PageHeading description={copy.lead} eyebrow={copy.eyebrow} title={copy.title} />
      <div className="legal-page__grid">
        <Card className="legal-content">
          <Scale aria-hidden="true" size={28} />
          <h2>Belge durumu</h2>
          {query.isPending ? <PageLoader label="Belge sürümü alınıyor…" /> : null}
          {query.isError ? (
            <ErrorState
              message={getErrorMessage(query.error)}
              onRetry={() => void query.refetch()}
            />
          ) : null}
          {query.data ? (
            <>
              {query.data.requiresLegalReview || !query.data.active ? (
                <InlineNotice title="Hukuk incelemesi gerekiyor" tone="warning">
                  Bu içerik geçici bir yer tutucudur; tüketici hukuku ve KVKK açısından profesyonel
                  inceleme tamamlanmadan yayın metni olarak kullanılmamalıdır.
                </InlineNotice>
              ) : (
                <InlineNotice title="Onaylı yayın sürümü" tone="positive">
                  Bu sayfa abonelik onayında kaydedilen etkin belge sürümünü gösterir.
                </InlineNotice>
              )}
              {query.data.content ? <p>{query.data.content}</p> : null}
              {query.data.contentUrl ? (
                <p>
                  <a href={query.data.contentUrl} rel="noreferrer" target="_blank">
                    Belgenin tam metnini aç
                  </a>
                </p>
              ) : null}
              <h2>Temel yaklaşım</h2>
              <ul className="check-list">
                {copy.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <p className="legal-version">
                Belge sürümü: <strong>{query.data.version}</strong> · Yayın tarihi:{' '}
                {formatDate(query.data.publishedAt)}
              </p>
            </>
          ) : null}
        </Card>
        <aside>
          <Card className="legal-aside">
            <ShieldCheck aria-hidden="true" size={24} />
            <h2>Ödeme güvenliği</h2>
            <p>
              Kart bilgileriniz güvenli ödeme kuruluşunun ekranında işlenir ve portal tarafından
              kaydedilmez.
            </p>
          </Card>
        </aside>
      </div>
    </article>
  );
}

export function NotFoundPage() {
  return (
    <section className="not-found">
      <div aria-hidden="true" className="not-found__number">
        404
      </div>
      <p className="eyebrow">Bu dalda bir sayfa yok</p>
      <h1>Aradığınız sayfa bulunamadı.</h1>
      <p>Bağlantı değişmiş olabilir; aboneliklerinize güvenle geri dönebilirsiniz.</p>
      <a className="button" href={`${env.shopifyStorefrontUrl}/account`}>
        <ArrowLeft aria-hidden="true" size={17} /> Aboneliklerim'e dön
      </a>
    </section>
  );
}
